import { ReactNode } from 'react'

interface CardProps {
  children: ReactNode
  className?: string
}

export function Card({ children, className = '' }: CardProps) {
  return (
    <div
      className={`bg-slate-900 border border-slate-800 rounded-lg p-6 hover:border-slate-700 transition-colors ${className}`}
    >
      {children}
    </div>
  )
}

interface StatCardProps {
  icon: string
  label: string
  value: string | number
  change?: string
  changeType?: 'up' | 'down'
}

export function StatCard({ icon, label, value, change, changeType }: StatCardProps) {
  return (
    <Card>
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="text-3xl">{icon}</div>
          {change && (
            <span
              className={`text-xs font-semibold px-2 py-1 rounded ${
                changeType === 'up'
                  ? 'bg-green-500/20 text-green-400'
                  : 'bg-red-500/20 text-red-400'
              }`}
            >
              {changeType === 'up' ? '↑' : '↓'} {change}
            </span>
          )}
        </div>
        <div>
          <p className="text-slate-400 text-sm font-medium">{label}</p>
          <p className="text-white text-2xl font-bold mt-1">{value}</p>
        </div>
      </div>
    </Card>
  )
}

interface ActionCardProps {
  icon: string
  title: string
  description: string
  onClick: () => void
}

export function ActionCard({ icon, title, description, onClick }: ActionCardProps) {
  return (
    <button
      onClick={onClick}
      className="block w-full text-left hover:scale-105 transition-transform duration-200"
    >
      <Card>
        <div className="space-y-3">
          <div className="text-4xl">{icon}</div>
          <div>
            <h3 className="text-white font-semibold">{title}</h3>
            <p className="text-slate-400 text-sm mt-1">{description}</p>
          </div>
        </div>
      </Card>
    </button>
  )
}

interface PageHeaderProps {
  icon: string
  title: string
  description?: string
}

export function PageHeader({ icon, title, description }: PageHeaderProps) {
  return (
    <div className="mb-8">
      <div className="flex items-center gap-4">
        <div className="text-5xl">{icon}</div>
        <div>
          <h1 className="text-3xl font-bold text-white">{title}</h1>
          {description && <p className="text-slate-400 text-sm mt-1">{description}</p>}
        </div>
      </div>
    </div>
  )
}

interface SectionProps {
  title: string
  children: ReactNode
}

export function Section({ title, children }: SectionProps) {
  return (
    <div className="space-y-4 mb-8">
      <h2 className="text-xl font-bold text-white">{title}</h2>
      {children}
    </div>
  )
}
